class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop =
      document.getElementById('ProductCountDesktop');

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );

    loadingSpinners.forEach((spinner) => {
      spinner.classList.remove('hidden');
    });

    const productGridContainer =
      document.getElementById('ProductGridContainer');

    const collection =
      productGridContainer?.querySelector('.collection');

    if (collection) {
      collection.classList.add('loading');
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url =
        `${window.location.pathname}` +
        `?section_id=${section.section}&${searchParams}`;

      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(
          filterDataUrl,
          event,
          updateEvent,
        );
      } else {
        FacetFiltersForm.renderSectionFromFetch(
          url,
          event,
          updateEvent,
        );
      }
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static startUpdateEvent(searchParams) {
    const {
      SearchUpdateEvent,
      CollectionUpdateEvent,
    } = window.StandardEvents || {};

    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm =
      document.querySelector('facet-filters-form');

    const facetsContainer =
      document.querySelector('.facets-container');

    const collectionId =
      facetsContainer?.dataset.collectionId || null;

    const collectionHandle =
      facetsContainer?.dataset.collectionHandle || '';

    const currentCount =
      parseInt(
        document.getElementById('ProductCount')
          ?.dataset.productCount,
      ) || 0;

    const urlSearchParams =
      new URLSearchParams(searchParams);

    const isSearchPage =
      facetsContainer?.dataset.template === 'search';

    const isCollectionPage =
      facetsContainer?.dataset.template === 'collection';

    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();

      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters:
              SearchUpdateEvent.parseProductFilters(
                urlSearchParams,
              ),
            sortKey:
              SearchUpdateEvent.getSortKey(
                urlSearchParams,
              ),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      deferred = CollectionUpdateEvent.createPromise();

      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: collectionId,
            handle: collectionHandle,
            productsCount: currentCount,
          },
          productFilters:
            CollectionUpdateEvent.parseProductFilters(
              urlSearchParams,
            ),
          sortKey:
            CollectionUpdateEvent.getSortKey(
              urlSearchParams,
            ),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({
          [isSearchPage
            ? 'totalCount'
            : 'productsCount']: filteredCount,
        });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(
    url,
    event,
    updateEvent,
  ) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];

        FacetFiltersForm.renderSection(
          html,
          event,
          updateEvent,
        );
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(
    filterDataUrl,
    event,
    updateEvent,
  ) {
    const html =
      FacetFiltersForm.filterData.find(
        filterDataUrl,
      ).html;

    FacetFiltersForm.renderSection(
      html,
      event,
      updateEvent,
    );
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(
      html,
      updateEvent,
    );

    if (
      typeof initializeScrollAnimationTrigger ===
      'function'
    ) {
      initializeScrollAnimationTrigger(
        html.innerHTML,
      );
    }
  }

  static renderProductGridContainer(html) {
    const currentContainer =
      document.getElementById(
        'ProductGridContainer',
      );

    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html',
      );

    const newContainer =
      parsedDocument.getElementById(
        'ProductGridContainer',
      );

    if (!currentContainer || !newContainer) return;

    currentContainer.innerHTML =
      newContainer.innerHTML;

    currentContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add(
          'scroll-trigger--cancel',
        );
      });

    document.dispatchEvent(
      new CustomEvent(
        'liora:product-grid-rendered',
      ),
    );
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml =
      new DOMParser().parseFromString(
        html,
        'text/html',
      );

    const sourceCount =
      parsedHtml.getElementById('ProductCount');

    const container =
      document.getElementById('ProductCount');

    const containerDesktop =
      document.getElementById(
        'ProductCountDesktop',
      );

    if (!sourceCount || !container) return;

    const count = sourceCount.innerHTML;

    container.innerHTML = count;

    container.dataset.productCount =
      sourceCount.dataset.productCount || '';

    container.dataset.totalCount =
      sourceCount.dataset.totalCount || '';

    container.classList.remove('loading');

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove(
        'loading',
      );
    }

    const loadingSpinners =
      document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
      );

    loadingSpinners.forEach((spinner) => {
      spinner.classList.add('hidden');
    });

    updateEvent?.resolve(
      parseInt(
        sourceCount.dataset.productCount,
      ) || 0,
    );
  }

  static renderFilters(html, event) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html',
      );

    const facetDetailsElementsFromFetch =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
      );

    const facetDetailsElementsFromDom =
      document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
      );

    Array.from(
      facetDetailsElementsFromDom,
    ).forEach((currentElement) => {
      const elementStillExists = Array.from(
        facetDetailsElementsFromFetch,
      ).some(({ id }) => {
        return currentElement.id === id;
      });

      if (!elementStillExists) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event
        ? event.target.closest('.js-filter')
        : undefined;

      return jsFilter
        ? element.id === jsFilter.id
        : false;
    };

    const facetsToRender = Array.from(
      facetDetailsElementsFromFetch,
    ).filter((element) => !matchesId(element));

    const countsToRender = Array.from(
      facetDetailsElementsFromFetch,
    ).find(matchesId);

    facetsToRender.forEach(
      (elementToRender, index) => {
        const currentElement =
          document.getElementById(
            elementToRender.id,
          );

        if (currentElement) {
          currentElement.innerHTML =
            elementToRender.innerHTML;
        } else {
          if (index > 0) {
            const {
              className:
                previousElementClassName,
              id: previousElementId,
            } = facetsToRender[index - 1];

            if (
              elementToRender.className ===
              previousElementClassName
            ) {
              const previousElement =
                document.getElementById(
                  previousElementId,
                );

              if (previousElement) {
                previousElement.after(
                  elementToRender,
                );
              }

              return;
            }
          }

          if (elementToRender.parentElement) {
            const parent =
              document.querySelector(
                `#${elementToRender.parentElement.id} .js-filter`,
              );

            if (parent) {
              parent.before(elementToRender);
            }
          }
        }
      },
    );

    FacetFiltersForm.renderActiveFacets(
      parsedHTML,
    );

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML,
    );

    if (countsToRender && event?.target) {
      const closestFilter =
        event.target.closest('.js-filter');

      const closestJSFilterID =
        closestFilter?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          closestFilter,
        );

        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(
            closestJSFilterID,
          ),
        );

        const newFacetDetailsElement =
          document.getElementById(
            closestJSFilterID,
          );

        if (!newFacetDetailsElement) return;

        const isTextInput =
          event.target.getAttribute('type') ===
          'text';

        if (!isTextInput) {
          const originatingInputId =
            event.target.id;

          const matchingInput =
            originatingInputId
              ? newFacetDetailsElement.querySelector(
                  `#${CSS.escape(
                    originatingInputId,
                  )}`,
                )
              : null;

          if (matchingInput) {
            matchingInput.focus({
              preventScroll: true,
            });
          } else {
            const fallbackSelector =
              newFacetDetailsElement.classList.contains(
                'mobile-facets__details',
              )
                ? '.mobile-facets__close-button'
                : '.facets__summary';

            const fallbackElement =
              newFacetDetailsElement.querySelector(
                fallbackSelector,
              );

            if (fallbackElement) {
              fallbackElement.focus({
                preventScroll: true,
              });
            }
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach(
      (selector) => {
        const activeFacetsElement =
          html.querySelector(selector);

        const currentElement =
          document.querySelector(selector);

        if (
          !activeFacetsElement ||
          !currentElement
        ) {
          return;
        }

        currentElement.innerHTML =
          activeFacetsElement.innerHTML;
      },
    );

    FacetFiltersForm.toggleActiveFacets(
      false,
    );
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach(
      (selector) => {
        const sourceElement =
          html.querySelector(selector);

        const currentElement =
          document.querySelector(selector);

        if (!sourceElement || !currentElement) {
          return;
        }

        currentElement.innerHTML =
          sourceElement.innerHTML;
      },
    );

    const mobileForm =
      document.getElementById(
        'FacetFiltersFormMobile',
      );

    const menuDrawer =
      mobileForm?.closest('menu-drawer');

    if (
      menuDrawer &&
      typeof menuDrawer.bindEvents ===
        'function'
    ) {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetSummary =
      target.querySelector(
        '.facets__summary',
      );

    const sourceSummary =
      source.querySelector(
        '.facets__summary',
      );

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML =
        sourceSummary.outerHTML;
    }

    const targetHeaderElement =
      target.querySelector(
        '.facets__header',
      );

    const sourceHeaderElement =
      source.querySelector(
        '.facets__header',
      );

    if (
      sourceHeaderElement &&
      targetHeaderElement
    ) {
      targetHeaderElement.outerHTML =
        sourceHeaderElement.outerHTML;
    }

    const targetWrapElement =
      target.querySelector('.facets-wrap');

    const sourceWrapElement =
      source.querySelector('.facets-wrap');

    if (
      sourceWrapElement &&
      targetWrapElement
    ) {
      const isShowingMore = Boolean(
        target.querySelector(
          'show-more-button .label-show-more.hidden',
        ),
      );

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(
            '.facets__item.hidden',
          )
          .forEach((hiddenItem) => {
            hiddenItem.classList.replace(
              'hidden',
              'show-more-item',
            );
          });
      }

      targetWrapElement.outerHTML =
        sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(
    source,
    target,
  ) {
    if (!source || !target) return;

    const targetFacetsList =
      target.querySelector(
        '.mobile-facets__list',
      );

    const sourceFacetsList =
      source.querySelector(
        '.mobile-facets__list',
      );

    if (
      sourceFacetsList &&
      targetFacetsList
    ) {
      targetFacetsList.outerHTML =
        sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const currentState =
      history.state &&
      typeof history.state === 'object'
        ? { ...history.state }
        : {};

    currentState.searchParams = searchParams;

    currentState.__lioraEntryId =
      `liora-${Date.now().toString(36)}-` +
      `${Math.random()
        .toString(36)
        .slice(2, 10)}`;

    history.pushState(
      currentState,
      '',
      `${window.location.pathname}${
        searchParams
          ? `?${searchParams}`
          : ''
      }`,
    );

    window.dispatchEvent(
      new CustomEvent(
        'liora:history-entry-change',
      ),
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid',
      );

    if (!productGrid?.dataset.id) {
      return [];
    }

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    return new URLSearchParams(
      formData,
    ).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(
      searchParams,
      event,
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms =
      document.querySelectorAll(
        'facet-filters-form form',
      );

    if (
      event.srcElement.className ===
      'mobile-facets__checkbox'
    ) {
      const searchParams =
        this.createSearchParams(
          event.target.closest('form'),
        );

      this.onSubmitForm(
        searchParams,
        event,
      );

      return;
    }

    const forms = [];

    const targetForm =
      event.target.closest('form');

    if (!targetForm) return;

    const isMobile =
      targetForm.id ===
      'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (
          form.id === 'FacetSortForm' ||
          form.id ===
            'FacetFiltersForm' ||
          form.id ===
            'FacetSortDrawerForm'
        ) {
          forms.push(
            this.createSearchParams(form),
          );
        }
      } else if (
        form.id ===
        'FacetFiltersFormMobile'
      ) {
        forms.push(
          this.createSearchParams(form),
        );
      }
    });

    this.onSubmitForm(
      forms.join('&'),
      event,
    );
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf(
        '?',
      ) === -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf(
              '?',
            ) + 1,
          );

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

customElements.define(
  'facet-filters-form',
  FacetFiltersForm,
);

FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll(
      'input',
    ).forEach((element) => {
      element.addEventListener(
        'change',
        this.onRangeChange.bind(this),
      );

      element.addEventListener(
        'keydown',
        this.onKeyDown.bind(this),
      );
    });

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(
      event.currentTarget,
    );

    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    if (!event.key.match(pattern)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll('input');

    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!minInput || !maxInput) return;

    if (maxInput.value) {
      minInput.setAttribute(
        'data-max',
        maxInput.value,
      );
    }

    if (minInput.value) {
      maxInput.setAttribute(
        'data-min',
        minInput.value,
      );
    }

    if (minInput.value === '') {
      maxInput.setAttribute(
        'data-min',
        0,
      );
    }

    if (maxInput.value === '') {
      minInput.setAttribute(
        'data-max',
        maxInput.getAttribute(
          'data-max',
        ),
      );
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);

    const min = Number(
      input.getAttribute('data-min'),
    );

    const max = Number(
      input.getAttribute('data-max'),
    );

    if (value < min) {
      input.value = min;
    }

    if (value > max) {
      input.value = max;
    }
  }
}

customElements.define(
  'price-range',
  PriceRange,
);

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector('a');

    if (!facetLink) return;

    facetLink.setAttribute(
      'role',
      'button',
    );

    facetLink.addEventListener(
      'click',
      this.closeFilter.bind(this),
    );

    facetLink.addEventListener(
      'keyup',
      (event) => {
        event.preventDefault();

        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          this.closeFilter(event);
        }
      },
    );
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest(
        'facet-filters-form',
      ) ||
      document.querySelector(
        'facet-filters-form',
      );

    if (form) {
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define(
  'facet-remove',
  FacetRemove,
);